import React from 'react';
import { PlayerHighlights } from '@/components/PlayerHighlights';
import { useGlobalMetrics } from '@/hooks/useGlobalMetrics';
import { useAnimatedCounter } from '@/hooks/useAnimatedCounter';
import { Target, Clock, Calendar, Hand } from 'lucide-react';

export function GlobalMetricsSection() {
  const { data: metrics, isLoading } = useGlobalMetrics();

  // Animated counters for compact stats
  const sessionsCounter = useAnimatedCounter({ 
    end: metrics?.totalSessions || 0, 
    duration: 2000,
    delay: 500 
  });

  const touchesCounter = useAnimatedCounter({ 
    end: metrics?.totalTouches || 0, 
    duration: 2000,
    delay: 600 
  });

  const hoursCounter = useAnimatedCounter({ 
    end: Math.round(metrics?.equivalentTrainingHours || 0), 
    duration: 2000,
    delay: 700 
  });

  const practicesCounter = useAnimatedCounter({ 
    end: metrics?.fullPracticesGained || 0, 
    duration: 2000,
    delay: 800 
  });

  const compactStats = [
    { icon: Target, value: sessionsCounter.value, label: 'Sessions' },
    { icon: Hand, value: touchesCounter.value, label: 'Touches' },
    { icon: Clock, value: hoursCounter.value, label: 'Hours', suffix: '+' },
    { icon: Calendar, value: practicesCounter.value, label: 'Practices' },
  ];

  return (
    <section className="py-8 md:py-14 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header - Matching HowWeDoIt style */}
        <div className="text-center mb-6 md:mb-10">
          <span className="inline-block px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium mb-3">
            Our Impact
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
            Real Results, Real Players
          </h2>
          <p className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto">
            See how FutWork is transforming player development
          </p>
        </div>

        {/* Compact Stats Row */}
        <div className="flex items-center justify-center gap-4 md:gap-8 mb-8 md:mb-10 flex-wrap">
          {compactStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-lg md:text-xl font-bold text-foreground tabular-nums">
                    {isLoading ? '—' : stat.value.toLocaleString()}{stat.suffix || ''}
                  </div>
                  <div className="text-[10px] md:text-xs text-muted-foreground -mt-0.5">
                    {stat.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Instagram Highlights */}
        <PlayerHighlights />
      </div>
    </section>
  );
}
